import {
  CadenceSlot,
  CallOutcome,
  LeadCadenceState,
  TouchpointChannel,
  buildCadenceAdvanceUpdate,
  buildCadenceLeadUpdate,
  buildInitialCadenceState,
  getLeadCreatedAt,
  inferSlotForDayChannel,
  markTouchpointComplete,
  markTouchpointSkipped,
  parseTouchpointKey,
  resolveLeadCadenceState,
} from 'lib/lead-cadence';

import {
  insertLeadTouchpointLog,
  upsertLeadCadenceState,
} from 'queries/lead-cadence';

type LeadLike = {
  id: string;
  metadata?: Record<string, unknown> | null;
  createdAt?: string | null;
  created_at?: string | null;
  cadence_state?: {
    id?: string | null;
  } | null;
};

type TouchpointAction = {
  day: number;
  slot?: CadenceSlot;
  channel: TouchpointChannel;
  status: 'completed' | 'skipped';
  skipReason?: string | null;
  skipNotes?: string | null;
  callOutcome?: string | null;
  leadContactId?: string | null;
  userId?: string | null;
};

type PersistCadenceOptions = {
  lead: LeadLike;
  state: LeadCadenceState;
  touchpoint?: TouchpointAction;
  upsertState: (options: {
    variables: { object: Record<string, unknown> };
  }) => Promise<unknown>;
  insertLog?: (options: {
    variables: { object: Record<string, unknown> };
  }) => Promise<unknown>;
  updateLead: (options: {
    variables: { id: string; data: Record<string, unknown> };
  }) => Promise<unknown>;
};

export async function persistCadenceState({
  lead,
  state,
  touchpoint,
  upsertState,
  insertLog,
  updateLead,
}: PersistCadenceOptions) {
  const startedAt = new Date(state.startedAt);

  const stateResult = await upsertState({
    variables: {
      object: {
        lead_id: lead.id,
        version: state.version,
        started_at: startedAt.toISOString(),
        paused: state.paused,
        paused_at: state.pausedAt,
      },
    },
  });

  const cadenceStateId =
    // @ts-expect-error Apollo result shape
    stateResult?.data?.insert_lead_cadence_state_one?.id ||
    lead.cadence_state?.id;

  if (touchpoint && insertLog) {
    await insertLog({
      variables: {
        object: {
          lead_id: lead.id,
          cadence_state_id: cadenceStateId,
          day: touchpoint.day,
          slot:
            touchpoint.slot ||
            inferSlotForDayChannel(touchpoint.day, touchpoint.channel),
          channel: touchpoint.channel,
          status: touchpoint.status,
          skip_reason: touchpoint.skipReason || null,
          skip_notes: touchpoint.skipNotes || null,
          call_outcome: touchpoint.callOutcome || null,
          lead_contact_id: touchpoint.leadContactId || null,
        },
      },
    });
  }

  await updateLead({
    variables: {
      id: lead.id,
      data: buildCadenceAdvanceUpdate(lead, state),
    },
  });
}

export async function initializeLeadCadence({
  lead,
  upsertState,
  updateLead,
  startedAt = getLeadCreatedAt(lead),
}: {
  lead: LeadLike;
  upsertState: PersistCadenceOptions['upsertState'];
  updateLead: PersistCadenceOptions['updateLead'];
  startedAt?: Date;
}) {
  const state = buildInitialCadenceState(startedAt);

  await upsertState({
    variables: {
      object: {
        lead_id: lead.id,
        version: state.version,
        started_at: startedAt.toISOString(),
        paused: false,
        paused_at: null,
      },
    },
  });

  await updateLead({
    variables: {
      id: lead.id,
      data: buildCadenceLeadUpdate(lead, startedAt),
    },
  });
}

export async function restartLeadCadence({
  lead,
  upsertState,
  updateLead,
  startedAt = new Date(),
}: {
  lead: LeadLike;
  upsertState: PersistCadenceOptions['upsertState'];
  updateLead: PersistCadenceOptions['updateLead'];
  startedAt?: Date;
}) {
  const state = buildInitialCadenceState(startedAt);

  await upsertState({
    variables: {
      object: {
        lead_id: lead.id,
        version: state.version,
        started_at: startedAt.toISOString(),
        paused: false,
        paused_at: null,
      },
    },
  });

  await updateLead({
    variables: {
      id: lead.id,
      data: buildCadenceAdvanceUpdate(lead, state, startedAt),
    },
  });
}

export async function completeCadenceTouchpoint({
  lead,
  day,
  channel,
  slot,
  upsertState,
  insertLog,
  updateLead,
  leadContactId,
  userId,
  callOutcome,
}: {
  lead: LeadLike;
  day: number;
  channel: TouchpointChannel;
  slot?: CadenceSlot;
  upsertState: PersistCadenceOptions['upsertState'];
  insertLog: PersistCadenceOptions['insertLog'];
  updateLead: PersistCadenceOptions['updateLead'];
  leadContactId?: string | null;
  userId?: string | null;
  callOutcome?: CallOutcome | null;
}) {
  const current =
    resolveLeadCadenceState(lead) ||
    buildInitialCadenceState(getLeadCreatedAt(lead));
  const resolvedSlot = slot || inferSlotForDayChannel(day, channel);
  const next = markTouchpointComplete(
    current,
    day,
    channel,
    callOutcome,
    resolvedSlot,
  );

  await persistCadenceState({
    lead,
    state: next,
    touchpoint: {
      day,
      slot: resolvedSlot,
      channel,
      status: 'completed',
      callOutcome: channel === 'call' ? callOutcome || null : null,
      leadContactId,
      userId,
    },
    upsertState,
    insertLog,
    updateLead,
  });
}

export async function skipCadenceTouchpoint({
  lead,
  day,
  channel,
  slot,
  skipReason,
  skipNotes,
  upsertState,
  insertLog,
  updateLead,
  userId,
}: {
  lead: LeadLike;
  day: number;
  channel: TouchpointChannel;
  slot?: CadenceSlot;
  skipReason: string;
  skipNotes?: string | null;
  upsertState: PersistCadenceOptions['upsertState'];
  insertLog: PersistCadenceOptions['insertLog'];
  updateLead: PersistCadenceOptions['updateLead'];
  userId?: string | null;
}) {
  const current =
    resolveLeadCadenceState(lead) ||
    buildInitialCadenceState(getLeadCreatedAt(lead));
  const resolvedSlot = slot || inferSlotForDayChannel(day, channel);
  const next = markTouchpointSkipped(
    current,
    day,
    channel,
    skipReason,
    skipNotes,
    resolvedSlot,
  );

  await persistCadenceState({
    lead,
    state: next,
    touchpoint: {
      day,
      slot: resolvedSlot,
      channel,
      status: 'skipped',
      skipReason,
      skipNotes,
      userId,
    },
    upsertState,
    insertLog,
    updateLead,
  });
}

export async function syncCompletedTouchpointLogs({
  lead,
  state,
  previousCompletedKeys,
  upsertState,
  insertLog,
  updateLead,
}: {
  lead: LeadLike;
  state: LeadCadenceState;
  previousCompletedKeys: string[];
  upsertState: PersistCadenceOptions['upsertState'];
  insertLog: NonNullable<PersistCadenceOptions['insertLog']>;
  updateLead: PersistCadenceOptions['updateLead'];
}) {
  const newKeys = state.completedKeys.filter(
    (key) => !previousCompletedKeys.includes(key),
  );

  for (const key of newKeys) {
    const parsed = parseTouchpointKey(key);
    if (!parsed) {
      continue;
    }

    const existingLog = state.touchpointLogs?.find((log) => log.key === key);
    await insertLog({
      variables: {
        object: {
          lead_id: lead.id,
          cadence_state_id: lead.cadence_state?.id,
          day: parsed.day,
          slot: parsed.slot,
          channel: parsed.channel,
          status: existingLog?.status || 'completed',
          skip_reason: existingLog?.skipReason || null,
          skip_notes: existingLog?.skipNotes || null,
          call_outcome: existingLog?.callOutcome || null,
        },
      },
    });
  }

  await persistCadenceState({
    lead,
    state,
    upsertState,
    updateLead,
  });
}

export { upsertLeadCadenceState, insertLeadTouchpointLog };
